/** Open only declared disclosure/tab paths; never click form actions.
 * @param {Array<string>} path outermost-to-innermost section labels
 * @param {string} modalId optional observed form identity
 * @param {number} timeoutMs maximum wait for React to render
 * @return {Promise<boolean>} whether every section became current
 */
export async function focusFormSection(path, modalId, timeoutMs = 2500) {
	if (!Array.isArray(path) || !path.length) return false;
	const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
	const visible = (node) => !node.closest('[hidden], [aria-hidden="true"]') &&
		window.getComputedStyle(node).display !== 'none' &&
		window.getComputedStyle(node).visibility !== 'hidden' &&
		node.getBoundingClientRect().width > 0;
	const deadline = Date.now() + timeoutMs;
	for (const label of path) {
		let reached = false;
		let requested = false;
		// Read the DOM before enforcing the deadline. React rendering or a
		// throttled browser timer can delay this check even after expansion
		// completed; the elapsed clock is not evidence that it failed.
		while (!reached) {
			const root = modalId ? [...document.querySelectorAll('[data-agent-modal-id]')]
				.find((node) => node.getAttribute('data-agent-modal-id') === modalId && visible(node)) : document;
			const matches = root ? [...root.querySelectorAll('[data-agent-section], [aria-expanded], [role="tab"]')]
				.filter((node) => visible(node) &&
					normalize(node.getAttribute('data-agent-section') || node.getAttribute('aria-label') || node.textContent) === normalize(label)) : [];
			// Duplicate visible labels are not permission to choose the first.
			if (matches.length === 1) {
				const node = matches[0];
				if (node.disabled || node.getAttribute('aria-disabled') === 'true') return false;
				const expanded = node.getAttribute('aria-expanded');
				const selected = node.getAttribute('aria-selected');
				if ((expanded === 'true' || selected === 'true') &&
					expanded !== 'false' && selected !== 'false') {
					reached = true;
					break;
				}
				if (!requested && Date.now() < deadline &&
					(expanded === 'false' || selected === 'false')) {
					requested = true;
					node.click();
					// Also observe synchronous renders before checking the budget.
					continue;
				}
			}
			const remaining = deadline - Date.now();
			if (remaining <= 0) break;
			await new Promise((resolve) => window.setTimeout(resolve, Math.min(100, remaining)));
		}
		if (!reached) return false;
	}
	return true;
}
